import React, { useEffect, useState, useMemo } from "react";
import { motion } from "framer-motion";
import countries from "world-countries";
import Flag from "react-world-flags";
import { IoIosCheckmarkCircle, IoMdSearch } from "react-icons/io";
import { FixedSizeList as List } from "react-window";
import CustomAction from "../../../../controls/CustomAction";
import { InputAdornment, TextField } from "@mui/material";
import useBookingApp from "../../../../../hooks/useBookingApp";
import { updateBookingField } from "../../../../../dispatchers/bookingDispatchers";

export default function AboutTrip({
  mainColor,
  setCountrySelectionMessages,
  handleNextStep,
}) {
  const { bookingSectionState } = useBookingApp();
  const { bookingModel } = bookingSectionState;

  const countryList = useMemo(
    () =>
      countries.map((country) => ({
        code: country.cca2,
        name: country.name.common,
      })),
    []
  );

  const [selectedCountryCodes, setSelectedCountryCodes] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [hasHydrated, setHasHydrated] = useState(false);

  const selectedCountryNames = useMemo(() => {
    return selectedCountryCodes
      .map((code) => {
        const country = countryList.find((c) => c.code === code);
        return country ? country.name : "";
      })
      .filter(Boolean);
  }, [selectedCountryCodes, countryList]);

  const handleAddCountry = (countryCode, countryName) => {
    if (!selectedCountryCodes.includes(countryCode)) {
      const updatedCodes = [...selectedCountryCodes, countryCode];
      setSelectedCountryCodes(updatedCodes);

      setCountrySelectionMessages((prev) => [
        ...prev,
        `Very Nice! You've added ${countryName} to your trip.`,
      ]);

      updateBookingField({
        field: "Countries",
        value: [...selectedCountryNames, countryName],
      });
    }
  };

  const handleRemoveCountry = (countryCode, countryName) => {
    const updatedCodes = selectedCountryCodes.filter(
      (code) => code !== countryCode
    );
    setSelectedCountryCodes(updatedCodes);

    // Compute new names immediately from updatedCodes
    const updatedNames = updatedCodes
      .map((code) => {
        const country = countryList.find((c) => c.code === code);
        return country ? country.name : "";
      })
      .filter(Boolean);

    setCountrySelectionMessages((prevMessages) => [
      ...prevMessages,
      `You've removed ${countryName} from your trip.`,
    ]);

    updateBookingField({
      field: "Countries",
      value: updatedNames,
    });

    // Optional: also update localStorage immediately (avoiding delay)
    if (hasHydrated) {
      localStorage.setItem("about-trip-data", JSON.stringify(updatedNames));
    }
  };

  useEffect(() => {
    const localStorageData =
      JSON.parse(localStorage.getItem("about-trip-data")) || [];
    const bookingModelNames = bookingModel?.Countries || [];

    const allNames = [...new Set([...localStorageData, ...bookingModelNames])];

    const matchedCountries = countryList.filter((country) =>
      allNames.includes(country.name)
    );

    setSelectedCountryCodes(matchedCountries.map((c) => c.code));

    setHasHydrated(true);
  }, [bookingModel, countryList]);

  useEffect(() => {
    if (hasHydrated) {
      localStorage.setItem(
        "about-trip-data",
        JSON.stringify(selectedCountryNames)
      );
    }
  }, [selectedCountryNames, hasHydrated]);

  const handleCountrySearch = (e) => {
    setSearchTerm(e.target.value.toLowerCase());
  };

  const filteredCountries = useMemo(
    () =>
      countryList.filter((country) =>
        country.name.toLowerCase().includes(searchTerm)
      ),
    [countryList, searchTerm]
  );

  const Row = ({ index, style }) => {
    const country = filteredCountries[index];
    const isSelected = selectedCountryCodes.includes(country.code);

    return (
      <div key={country.code} style={style} className="bf-country-box">
        <div className="country-flag-wrapper">
          <div className="flag-container">
            <Flag code={country.code} />
          </div>
          <p className="country-name">{country.name}</p>
        </div>

        {isSelected ? (
          <IoIosCheckmarkCircle
            className="checkmark"
            color={mainColor}
            size={22}
            onClick={() => handleRemoveCountry(country.code, country.name)}
          />
        ) : (
          <div
            className="empty-circle"
            onClick={() => handleAddCountry(country.code, country.name)}
          ></div>
        )}
      </div>
    );
  };

  return (
    <motion.div
      initial={{ y: 50, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ type: "spring", stiffness: 100, delay: 0.5 }}
      exit={{ y: 50, opacity: 0 }}
      className="bf-content-wrapper trip-traveling-wrapper"
    >
      <div className="bf-content-header">
        <h2 className="bf-title" style={{ color: mainColor }}>
          Where are you traveling?
        </h2>
        <p className="bf-subtitle">
          Choose your travel destination(s) so we can ensure you're covered with
          the right vaccines and health advice.
        </p>
      </div>

      <div className="bf-content-body">
        <div className="bf-country-search">
          <TextField
            className="bf-form-field"
            id="searchCountry"
            name="searchCountry"
            label="Search country by name..."
            variant="filled"
            fullWidth
            onChange={handleCountrySearch}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IoMdSearch size={22} />
                </InputAdornment>
              ),
            }}
          />
        </div>
        <div className="bf-country-picker-wrapper fp">
          {filteredCountries.length > 0 ? (
            <List
              height={window.innerHeight - 200}
              itemCount={filteredCountries.length}
              itemSize={50}
              width="100%"
            >
              {Row}
            </List>
          ) : (
            <div className="no-country-found">
              <p>No country found</p>
            </div>
          )}
        </div>
      </div>

      <CustomAction
        text={`${selectedCountryCodes.length} ${
          selectedCountryCodes.length === 1 ? "country" : "countries"
        } selected`}
        mainColor={mainColor}
        action={() => handleNextStep()}
        disabled={selectedCountryCodes.length === 0}
      />
    </motion.div>
  );
}
